/**
 * A tiny keyed cache for whole-page payloads, so leaving a page and coming back can render what
 * was on screen last time instead of a spinner, then refresh underneath the reader.
 *
 * In memory ONLY — deliberately not sessionStorage and not localStorage. This is a navigation
 * convenience: a hard reload is exactly the moment a user expects fresh data, and an entry that
 * survived one would turn a fast page into a mysteriously stale one. It also means nothing about
 * one user's data outlives the tab, which is the other half of why storage is the wrong home.
 *
 * Generic on purpose so another page can adopt it, but no page other than the dashboard uses it
 * yet — the key format is each caller's own business.
 */

export interface CachedPage<T> {
    data: T;
    /** ms since the epoch — what an "Updated 3 min ago" label is built from. */
    fetchedAt: number;
}

/**
 * Enough for a user flicking between a few filter sets and pages; small enough that paging
 * through filters all afternoon cannot grow this without bound. Whole API responses live in
 * here, so the cap is a memory ceiling, not a tidiness preference.
 */
const MAX_ENTRIES = 12;

const store = new Map<string, CachedPage<unknown>>();

export function get<T>(key: string): CachedPage<T> | undefined {
    return store.get(key) as CachedPage<T> | undefined;
}

export function set<T>(key: string, data: T): void {
    // Delete before inserting so a re-fetched key moves to the END of the Map's insertion order.
    // Without that, eviction would drop the entry that was written first ever rather than the one
    // written least recently, and the page a user keeps returning to would be the one evicted.
    store.delete(key);
    store.set(key, { data, fetchedAt: Date.now() });

    while (store.size > MAX_ENTRIES) {
        const oldest = store.keys().next();
        if (oldest.done) break;
        store.delete(oldest.value);
    }
}

/**
 * Rewrite one entry's data in place, keeping its original `fetchedAt`.
 *
 * For the case where the page already knows what changed — a row the user just saved — and wants
 * the cache to agree with the screen without pretending a fetch happened. Refreshing the
 * timestamp here would make the "Updated N ago" label lie about how old the rest of the entry is.
 * A miss is a no-op: there is nothing to keep in step.
 */
export function update<T>(key: string, updater: (data: T) => T): void {
    const existing = store.get(key) as CachedPage<T> | undefined;
    if (!existing) return;
    store.set(key, { data: updater(existing.data), fetchedAt: existing.fetchedAt });
}

/** Every live key, optionally narrowed to one caller's prefix. */
export function keys(prefix?: string): string[] {
    const all = Array.from(store.keys());
    return prefix === undefined ? all : all.filter((key) => key.startsWith(prefix));
}

/** Drop every entry under a prefix — one page's worth, or one business's worth. */
export function clear(prefix: string): void {
    for (const key of keys(prefix)) store.delete(key);
}

/**
 * Drop everything. Called on logout, where the next person at this browser must not see a frame
 * of the last one's data, and by tests, where a module-level Map otherwise leaks between cases.
 */
export function clearAll(): void {
    store.clear();
}
